import { Injectable } from '@angular/core';
import markdownToTxt from 'markdown-to-txt';
import {environment} from "@app/environment";
import {UserService} from "../../../core/user.service";

export type SpeechEngine = 'polly' | 'browser';
export type SpeechLanguage = 'it' | 'en';
type PollySpeechEngine = 'standard' | 'neural' | 'long-form' | 'generative';

export interface SpeechLanguageConfig {
  languageCode: string;
  polly: {
    voiceId: string;
    engine: PollySpeechEngine;
  };
  browserVoiceNames: string[];
}

@Injectable({ providedIn: 'root' })
export class SpeechStreamService {
  private buffer = '';
  private synth = window.speechSynthesis;
  private defaultVoice: SpeechSynthesisVoice | null = null;
  private readonly engine: SpeechEngine = environment.FEATURE_FLAGS.coso.enable_polly ? 'polly' : 'browser';
  private selectedLanguage: SpeechLanguage = 'it';
  private queue: Promise<void> = Promise.resolve();
  private pollyQueue: {
    generation: number;
    audioPromise: Promise<Blob>;
  }[] = [];
  private pollyPlaybackRunning = false;
  private generation = 0;
  private pollyAudio = new Audio();
  private pollyAbortControllers = new Set<AbortController>();
  private activeObjectUrl?: string;
  private resolvePollyPlayback?: () => void;
  private doneResolvers: (() => void)[] = [];

  private readonly POLLY_TEXT_CHAR_LIMIT = 3000;
  private readonly SILENT_WAV_DATA_URI = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=';

  constructor(private userService: UserService) {
    this.initVoice();
  }

  public setLanguage(language: SpeechLanguage) {
    if (this.selectedLanguage === language) {
      return;
    }

    this.selectedLanguage = language;
    this.setDefaultVoice();
    this.stop();
  }

  private initVoice() {
    this.setDefaultVoice();
    this.synth.onvoiceschanged = () => this.setDefaultVoice();
  }

  private setDefaultVoice() {
    const voices = this.synth.getVoices();
    const config = this.getLanguageConfig();
    const languagePrefix = this.selectedLanguage;

    this.defaultVoice = voices.find(v => config.browserVoiceNames.includes(v.name)) ||
      voices.find(v => v.lang === config.languageCode) ||
      voices.find(v => v.lang.startsWith(languagePrefix)) ||
      null;

    console.log('Voice selected:', this.defaultVoice?.name);
  }

  private getLanguageConfig(): SpeechLanguageConfig {
    return environment.COSO_SPEECH_LANGUAGE_CONFIG[this.selectedLanguage];
  }

  public unlockAudio() {
    // Create a completely silent, brief utterance
    const utterance = new SpeechSynthesisUtterance(' ');
    utterance.volume = 0; // Keep it silent
    utterance.rate = 10;  // Make it finish instantly

    this.synth.speak(utterance);

    // Unlock the reusable audio element used by Polly playback.
    this.pollyAudio.muted = true;
    this.pollyAudio.src = this.SILENT_WAV_DATA_URI;
    void this.pollyAudio.play()
      .then(() => {
        this.pollyAudio.pause();
        this.pollyAudio.currentTime = 0;
        this.pollyAudio.muted = false;
        this.pollyAudio.removeAttribute('src');
      })
      .catch(() => {
        this.pollyAudio.muted = false;
      });

    console.log('Audio engine unlocked for iOS');
  }

  // Stream a string chunk into the processor
  processChunk(chunk: string) {
    this.buffer += chunk;

    // Look for sentence boundaries: . ! ? or double newlines
    const sentenceEndRegex = /[.!?]\s|\n\n/g;
    let match;

    while ((match = sentenceEndRegex.exec(this.buffer)) !== null) {
      const sentenceEndIndex = match.index + 1;
      const fullSentence = this.buffer.substring(0, sentenceEndIndex).trim();

      if (fullSentence) {
        this.queueSpeech(fullSentence);
      }

      // Remove the processed sentence from the buffer
      this.buffer = this.buffer.substring(sentenceEndIndex);
      sentenceEndRegex.lastIndex = 0; // Reset regex for the new shortened buffer
    }
  }

  private queueSpeech(rawMarkdown: string) {
    // Convert MD to plain text
    const cleanText = this.cleanTextForSpeech(markdownToTxt(rawMarkdown));
    if (!cleanText.trim()) {
      return;
    }

    if (this.engine === 'polly') {
      this.queuePollySpeech(cleanText);
      return;
    }

    const generation = this.generation;
    this.queue = this.queue
      .then(() => {
        if (generation !== this.generation) {
          return;
        }

        this.speakWithBrowser(cleanText);
      })
      .catch((err) => {
        console.error('Speech synthesis failed:', err);
      });
  }

  private cleanTextForSpeech(text: string): string {
    return text
      .replace(/\[\[ATTACHMENT_REF:[^\]]*(?:\]\])?/g, '')
      .replace(/\p{Extended_Pictographic}/gu, '')
      .replace(/\p{Emoji_Presentation}/gu, '')
      .replace(/\p{Variation_Selector}/gu, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  private speakWithBrowser(cleanText: string) {
    // Create utterance
    const utterance = new SpeechSynthesisUtterance(cleanText);
    const config = this.getLanguageConfig();

    // Safari needs a specific locale string to pick the right language.
    utterance.lang = config.languageCode;

    if (this.defaultVoice) {
      utterance.voice = this.defaultVoice;
    }

    // 3. Safari Tip: Set volume explicitly.
    // Sometimes Safari defaults to 0 if not interacted with.
    utterance.volume = 1;

    // Optional: Set voice/rate
    utterance.pitch = 1.0;
    utterance.rate = 1.0;

    // Queue it (The browser handles the queueing automatically)
    this.synth.speak(utterance);
  }

  private queuePollySpeech(cleanText: string) {
    const generation = this.generation;
    const chunks = this.splitForPolly(cleanText);
    const languageConfig = this.getLanguageConfig();

    for (const chunk of chunks) {
      if (generation !== this.generation) {
        break;
      }

      // Start the network/synthesis request now; playback still consumes in order.
      this.pollyQueue.push({
        generation,
        audioPromise: this.fetchPollyAudio(chunk, languageConfig)
      });
    }

    this.drainPollyQueue();
  }

  private drainPollyQueue() {
    if (this.pollyPlaybackRunning) {
      return;
    }

    this.pollyPlaybackRunning = true;
    void this.playQueuedPollyAudio(this.generation);
  }

  private async playQueuedPollyAudio(generation: number) {
    while (generation === this.generation && this.pollyQueue.length) {
      const item = this.pollyQueue.shift()!;
      if (item.generation !== generation) {
        continue;
      }

      try {
        const audioBlob = await item.audioPromise;
        if (generation !== this.generation) {
          continue;
        }

        await this.playPollyAudio(audioBlob, generation);
      } catch (err) {
        if (generation === this.generation) {
          console.warn('Polly speech failed:', err);
        }
      }
    }

    if (generation !== this.generation) {
      return;
    }

    this.pollyPlaybackRunning = false;

    if (this.pollyQueue.length) {
      this.drainPollyQueue();
    } else {
      // Queue fully drained — notify all waiters
      const resolvers = this.doneResolvers.splice(0);
      resolvers.forEach(r => r());
    }
  }

  private async fetchPollyAudio(text: string, languageConfig: SpeechLanguageConfig): Promise<Blob> {
    const authToken = await this.userService.getJWT();
    if (!authToken) {
      throw new Error('Missing Cognito JWT');
    }

    const controller = new AbortController();
    this.pollyAbortControllers.add(controller);
    const response = await fetch(environment.COSO_API_URL + '/synthesizeSpeech', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'api-key': environment.COSO_API_KEY,
        'Authorization': `Bearer ${authToken}`,
        'ngsw-bypass': 'true'
      },
      body: JSON.stringify({
        text,
        voiceId: languageConfig.polly.voiceId,
        engine: languageConfig.polly.engine,
        languageCode: languageConfig.languageCode,
        outputFormat: 'mp3'
      }),
      signal: controller.signal
    })
      .finally(() => {
        this.pollyAbortControllers.delete(controller);
      });

    if (!response.ok) {
      throw new Error(await response.text() || `Polly request failed with status ${response.status}`);
    }

    return response.blob();
  }

  private playPollyAudio(audioBlob: Blob, generation: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.resolvePollyPlayback?.();
      this.resolvePollyPlayback = resolve;
      this.revokeActiveObjectUrl();
      this.activeObjectUrl = URL.createObjectURL(audioBlob);
      this.pollyAudio.src = this.activeObjectUrl;
      this.pollyAudio.muted = false;

      this.pollyAudio.onended = () => {
        this.resolvePollyPlayback = undefined;
        this.revokeActiveObjectUrl();
        resolve();
      };
      this.pollyAudio.onerror = () => {
        this.resolvePollyPlayback = undefined;
        this.revokeActiveObjectUrl();
        reject(new Error('Unable to play Polly audio.'));
      };

      if (generation !== this.generation) {
        this.revokeActiveObjectUrl();
        resolve();
        return;
      }

      void this.pollyAudio.play().catch((err) => {
        this.resolvePollyPlayback = undefined;
        this.revokeActiveObjectUrl();
        reject(err);
      });
    });
  }

  private splitForPolly(text: string): string[] {
    const chunks: string[] = [];
    let remainingText = text.trim();

    while (remainingText.length > this.POLLY_TEXT_CHAR_LIMIT) {
      const splitIndex = this.findPollySplitIndex(remainingText);
      chunks.push(remainingText.slice(0, splitIndex).trim());
      remainingText = remainingText.slice(splitIndex).trim();
    }

    if (remainingText) {
      chunks.push(remainingText);
    }

    return chunks;
  }

  private findPollySplitIndex(text: string): number {
    const limit = this.POLLY_TEXT_CHAR_LIMIT;
    const preferredSplit = Math.max(
      text.lastIndexOf('. ', limit),
      text.lastIndexOf('! ', limit),
      text.lastIndexOf('? ', limit),
      text.lastIndexOf('\n', limit)
    );
    if (preferredSplit > limit * 0.5) {
      return preferredSplit + 1;
    }

    const whitespaceSplit = text.lastIndexOf(' ', limit);
    return whitespaceSplit > 0 ? whitespaceSplit : limit;
  }

  private revokeActiveObjectUrl() {
    if (this.activeObjectUrl) {
      URL.revokeObjectURL(this.activeObjectUrl);
      this.activeObjectUrl = undefined;
    }
  }

  flush(): Promise<void> {
    const remainingText = this.buffer.trim();
    if (remainingText) {
      this.queueSpeech(remainingText);
    }
    this.buffer = ''; // Reset for the next stream

    return this.whenDone();
  }

  private whenDone(): Promise<void> {
    if (this.engine === 'polly') {
      return new Promise<void>(resolve => {
        if (!this.pollyPlaybackRunning && this.pollyQueue.length === 0) {
          resolve();
          return;
        }
        this.doneResolvers.push(resolve);
      });
    } else {
      // Browser engine: chain onto the existing queue promise
      return this.queue.then(() => {});
    }
  }

  stop() {
    this.generation++;
    this.queue = Promise.resolve();
    this.pollyQueue = [];
    this.pollyPlaybackRunning = false;
    this.pollyAbortControllers.forEach(controller => controller.abort('User aborted'));
    this.pollyAbortControllers.clear();
    this.resolvePollyPlayback?.();
    this.resolvePollyPlayback = undefined;
    this.pollyAudio.pause();
    this.pollyAudio.removeAttribute('src');
    this.revokeActiveObjectUrl();
    this.synth.cancel();
    this.buffer = '';

    // Resolve any pending done-waiters immediately since speech was cancelled
    const resolvers = this.doneResolvers.splice(0);
    resolvers.forEach(r => r());
  }
}
